import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { KibField } from '@chewy/kib-fields-new-react'
import { PuiCheckbox } from '@pbt/pbt-ui-components'

import RhapKibDatePicker from '~/components/common/inputs/kib-date-picker/RhapKibDatePicker'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getInventoryDamageReasons } from '~/store/reducers/constants'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import type { ReceiptFields } from './hooks/useReceiptFields'

import '@chewy/kib-datepicker-styles/dist/kib-datepicker.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './OrderItemReceipt.module.scss'

interface OrderItemReceiptProps {
  fields: ReceiptFields
  isDraft?: boolean
  isViewOnly?: boolean
  orderedQuantity: number
  perPackageAmount: number
  perPackageLabel: string
  productSizeUnit: string
}

export const OrderItemReceipt = ({
  fields,
  perPackageLabel,
  isDraft = false,
  isViewOnly = false,
}: OrderItemReceiptProps) => {
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const InventoryDamageReasons = useSelector(getInventoryDamageReasons)
  const timezone = useCurrentBusinessTimezone()

  const {
    receivedQuantity,
    lotNumber,
    expirationDate,
    serialNumber,
    businessReferenceNumber,
    damaged,
    damageReasonId,
  } = fields

  // Set damage reason id to first option in list if it's not already set on draft receipt
  useEffect(() => {
    if (isDraft && damaged.value && !damageReasonId.value) {
      damageReasonId.setValue(InventoryDamageReasons[0].id)
    }
  }, [isDraft, damaged.value, damageReasonId.value])

  const inputDisabled = !isDraft || isUpdating || isViewOnly

  return (
    <div className={styles.container}>
      <div className={styles.fieldsRow}>
        <div className={styles.quantityWrapper}>
          <KibField
            autoFocus
            required
            disabled={inputDisabled}
            invalid={receivedQuantity.errors.length > 0}
            label={receivedQuantity.label!}
            messagesContent={receivedQuantity.message}
            min={0}
            type="number"
            value={receivedQuantity.value ?? ''}
            onChange={(e) => receivedQuantity.setValue(Number(e.target.value))}
          />
          <span className={styles.quantityLabel}>{perPackageLabel}</span>
        </div>

        <KibField
          disabled={inputDisabled}
          label={lotNumber.label!}
          value={lotNumber.value ?? ''}
          onChange={(e) => lotNumber.setValue(e.target.value)}
        />

        <RhapKibDatePicker
          disabled={inputDisabled}
          label={expirationDate.label}
          renderInputNew={(props) => <KibField {...props} />}
          timezone={timezone}
          value={expirationDate.value}
          onChange={(date) => expirationDate.setValue(date)}
        />

        <KibField
          disabled={inputDisabled}
          label={serialNumber.label!}
          value={serialNumber.value ?? ''}
          onChange={(e) => serialNumber.setValue(e.target.value)}
        />

        <KibField
          disabled={inputDisabled}
          label={businessReferenceNumber.label!}
          value={businessReferenceNumber.value ?? ''}
          onChange={(e) => businessReferenceNumber.setValue(e.target.value)}
        />
      </div>
      <div>
        <PuiCheckbox
          disabled={inputDisabled}
          field={damaged}
          label={damaged.label}
        />
      </div>
    </div>
  )
}
